import useSpaceStore from '../../stores/spaceStore';
import { useSpatialUIStore } from '../../stores/spaceSpatialUIStore';
import { useWindowDragIndicatorStore } from '../../stores/windowDragIndicatorStore';
import { layoutFamilyOf } from '../../components/Space/utils/windowUtils';
import { AnyWindowEntity } from '../../types/windows';
import { CRDTServiceWS, getCRDTServiceInstance } from './crdtServiceWS';
import { createFileLogger } from '../../utils/logger';
import * as Y from '../../shared/yjs';
import type { WebsocketProvider } from 'y-websocket';

export type SharedWindowsConnectionStatus = 'connected' | 'connecting' | 'disconnected';

const log = createFileLogger('SHARED_SPACE_WINDOWS_CRDT_SERVICE', { enabled: false });

/**
 * Shared, multi-collaborator window layout for a Space's canvas.
 *
 * This is the SIBLING of `spaceCRDTService`, not a replacement for it:
 *   - `spaceCRDTService` owns per-user session state (settings, spatial-canvas
 *     camera, chat tabs) in the `space:{spaceId}:{userId}` room — one Y.Doc
 *     per (space, user) pair, invisible to every other user.
 *   - `SharedSpaceWindowsCRDTService` (this file) owns WINDOW layout
 *     (position, size, content, tabs, z-order, ...) in the
 *     `space-windows:{spaceId}` room — ONE Y.Doc per space, shared by every
 *     collaborator with access to it. This is what makes two people on the
 *     same spatial or spatialCanvas view see and manipulate the same windows
 *     in real time.
 *
 * Known scope limitation: `window.position`/`window.size` are still single,
 * shared fields (mirroring how `Window.tsx`/`WindowContainer` already read
 * them). If two collaborators are simultaneously in DIFFERENT layout
 * families (one in `spatial`, one in `spatialCanvas`), spaceStore's
 * `applyLayoutForViewMode` cascade — triggered by either collaborator's own
 * viewMode switch — reprojects these shared fields to match WHOEVER
 * switched last, which can visibly move windows for the other collaborator.
 * Same-family collaboration (both spatial, or both spatialCanvas — the
 * primary multiplayer scenario) is unaffected. Solving mixed-family
 * multiplayer would mean sharing `window.layouts.{family}` as independent
 * per-family shared slices and changing every render path to read the
 * viewer's own current family instead of the top-level fields — a larger,
 * separate change intentionally left out of this pass.
 *
 * CRDT design, mirrored from spaceCRDTService's windows handling:
 *   - Each window is a NESTED Y.Map (not a plain object) inside the shared
 *     `windows` Y.Map, so two collaborators editing DIFFERENT fields of the
 *     SAME window (e.g. one dragging `position` while another renames
 *     `title`) merge per-field instead of the whole object clobbering.
 *   - All multi-field writes happen inside a single `doc.transact()` so
 *     observers see one atomic update, never a partially-applied window set.
 *   - `updatedAt` is stamped on every write and used as a stale-overwrite
 *     guard: a delayed remote snapshot can never revert a window to an
 *     older state than what's already applied locally.
 *   - Every local write is tagged with `localOriginId` so this tab's own
 *     Y.Doc observer ignores its own writes (no local echo loop).
 */

// ✅ CRITICAL: Add origin tag so we can distinguish local vs remote writes
// on the shared doc. Unlike the per-user service's localOriginId (unique per
// browser tab), this only needs to be unique per this JS module instance —
// multiple tabs of the same OR different users are all "remote" to each other.
const crdtServiceWS = getCRDTServiceInstance();

const normalizeUpdatedAt = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Throttle live CRDT writes during drag — the settled position is committed
// once on drag-stop via the regular updateWindow() path; streamWindowDragPosition
// only needs to be "smooth enough", not lossless.
const throttledDragUpdates = new Map<string, { lastUpdate: number; pendingUpdate: boolean }>();
const DRAG_THROTTLE_MS = 80;

function shouldThrottleDragUpdate(windowId: string): boolean {
  const now = performance.now();
  const state = throttledDragUpdates.get(windowId);
  if (!state) {
    throttledDragUpdates.set(windowId, { lastUpdate: now, pendingUpdate: false });
    return false;
  }
  const elapsed = now - state.lastUpdate;
  if (elapsed < DRAG_THROTTLE_MS) {
    if (!state.pendingUpdate) {
      state.pendingUpdate = true;
      setTimeout(() => {
        state.lastUpdate = 0;
        state.pendingUpdate = false;
      }, DRAG_THROTTLE_MS - elapsed + 16);
    }
    return true;
  }
  state.lastUpdate = now;
  return false;
}

export interface SharedSpaceConnectionInfo {
  spaceId: string;
  connectionCount: number;
  providerStatus?: string;
  lastUpdate: number;
}

export interface DraggerIdentity {
  userId: string;
  name: string;
  color: string;
}

interface DragBroadcastEntry {
  x: number;
  y: number;
  userId?: string;
  name?: string;
  color?: string;
}

// Safety net for a dragger's tab crashing/losing connection mid-drag without
// ever broadcasting the explicit "drag ended" removal — see
// windowDragIndicatorStore.clearStale.
const DRAG_INDICATOR_STALE_MS = 2000;
const DRAG_INDICATOR_SWEEP_MS = 1000;

export class SharedSpaceWindowsCRDTService {
  initializedSpaces = new Set<string>();
  private initializedSpaceKeys = new Map<string, { windowsKey: string; dragKey: string }>();
  private spaceObservers = new Map<string, () => void>();
  private dragReceivers = new Map<string, () => void>();
  private connectionListeners = new Map<string, ((info: SharedSpaceConnectionInfo) => void)[]>();
  private connectionPollIntervals = new Map<string, ReturnType<typeof setInterval>>();
  // Same reentrancy-guard shape as spaceCRDTService.initializeSpace — a fast
  // remount / duplicate effect fire must await the FIRST call's promise
  // instead of racing a second seed against it.
  private initializingSpaces = new Map<string, Promise<void>>();
  private localOriginId = `shared-tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  constructor(private crdt: CRDTServiceWS) {
    (window as any).sharedSpaceWindows = {
      dumpWindows: (spaceId: string) => {
        const doc = this.crdt.getYDoc(this.getWindowsKey(spaceId));
        if (!doc) {
          log.error('[Test] No Y.Doc for space:', spaceId);
          return;
        }
        const windows: Record<string, any> = {};
        doc.getMap('windows').forEach((w: any, wid: string) => {
          windows[wid] = w?.toJSON ? w.toJSON() : w;
        });
        log.info('[Test] Shared windows for', spaceId, windows);
        return windows;
      },
      getConnectionInfo: (spaceId: string) => this.getSpaceConnectionInfo(spaceId),
    };
  }

  private getWindowsKey(spaceId: string): string {
    return `space-windows:${spaceId}`;
  }

  private getDragKey(spaceId: string): string {
    return `space-window-drag:${spaceId}`;
  }

  /**
   * Write window fields into the shared `windows` Y.Map using a nested Y.Map
   * per window, so concurrent collaborators editing DIFFERENT fields merge
   * per-field instead of one whole-object write clobbering the other. MUST
   * be called inside a `doc.transact()`.
   */
  private mergeWindowFields(windowsMap: Y.Map<any>, windowId: string, fields: Record<string, any>) {
    let entry = windowsMap.get(windowId);
    if (!(entry instanceof Y.Map)) {
      const ymap = new Y.Map();
      if (entry && typeof entry === 'object') {
        for (const [k, v] of Object.entries(entry as Record<string, any>)) {
          if (v !== undefined) ymap.set(k, v);
        }
      }
      windowsMap.set(windowId, ymap);
      entry = ymap;
    }
    for (const [k, v] of Object.entries(fields)) {
      if (v !== undefined) (entry as Y.Map<any>).set(k, v);
    }
  }

  removeWindow(spaceId: string, windowId: string) {
    log.info('[SharedSpaceWindows] Removing window:', { spaceId, windowId });

    const isDraggingThisWindow = (window as any).__windowDragging &&
      (window as any).__lastDragWindowId === windowId;
    if (isDraggingThisWindow) {
      log.info('[SharedSpaceWindows] Deferring window removal - drag operation in progress');
      setTimeout(() => this.removeWindow(spaceId, windowId), 200);
      return;
    }

    const windowsKey = this.getWindowsKey(spaceId);
    const doc = this.crdt.getYDoc(windowsKey);
    if (doc) {
      try {
        doc.transact(() => {
          doc.getMap('windows').delete(windowId);
        }, this.localOriginId);
      } catch (transactErr) {
        const errName = (transactErr as DOMException)?.name;
        if (errName === 'InvalidStateError') {
          log.warn('[SharedSpaceWindows] IDB connection closing during removeWindow, retrying:', windowId);
          setTimeout(() => {
            try {
              const retryDoc = this.crdt.getYDoc(windowsKey);
              retryDoc?.transact(() => {
                retryDoc.getMap('windows').delete(windowId);
              }, this.localOriginId);
            } catch {
              // Page is tearing down — safe to ignore
            }
          }, 300);
        } else {
          log.error('[SharedSpaceWindows] Unexpected error during removeWindow transact:', transactErr);
        }
      }
    } else {
      log.warn('[SharedSpaceWindows] No Y.Doc found for space, window not removed:', spaceId);
    }

    setTimeout(() => {
      const laterWindows = useSpaceStore.getState().windows;
      if (laterWindows[windowId]) {
        log.warn(`[SharedSpaceWindows] Window ${windowId} still in store 1000ms after removal — force-removing locally`);
        const updatedWindows = { ...laterWindows };
        delete updatedWindows[windowId];
        useSpaceStore.getState().setWindows(Object.values(updatedWindows));
      }
    }, 1000);
  }

  /**
   * Broadcast a viewMode change to every OTHER collaborator viewing this
   * Space, by writing it into the shared `meta` map alongside `windows` — see
   * the class docstring for why this reuses the windows room instead of a new
   * one. Mirrors spaceCRDTService.updateSettings's transact pattern so this
   * tab's own setupSpaceObserver ignores the echo (origin === localOriginId).
   */
  broadcastViewMode(spaceId: string, viewMode: string) {
    const windowsKey = this.getWindowsKey(spaceId);
    const doc = this.crdt.getYDoc(windowsKey);
    if (!doc) {
      log.warn('[SharedSpaceWindows] No Y.Doc found for space, cannot broadcast viewMode:', spaceId);
      return;
    }
    try {
      doc.transact(() => {
        doc.getMap('meta').set('viewMode', viewMode);
      }, this.localOriginId);
    } catch (error) {
      log.error(`[SharedSpaceWindows] Error broadcasting viewMode for space ${spaceId}:`, error);
    }
  }

  updateWindow(spaceId: string, windowId: string, updates: Partial<AnyWindowEntity>) {
    const isDragOperation = !!updates.position && (window as any).__windowDragging;

    if (updates.position) {
      const store = useSpaceStore.getState();
      if (isDragOperation && store.windows[windowId] && shouldThrottleDragUpdate(windowId)) {
        return;
      }
    }

    // Drag-in-progress: stream to the live broadcast room (zero persistence,
    // near-zero latency) AND commit to the durable room so a mid-drag
    // disconnect/reconnect never loses the window entirely.
    if (isDragOperation && updates.position) {
      try {
        this.crdt.update(this.getDragKey(spaceId), { [windowId]: updates.position }, {
          updateType: 'update',
          preserveOtherConnections: true,
          preserveNoteConnections: true,
        });
      } catch (error) {
        log.error(`[SharedSpaceWindows] Error streaming drag position for window ${windowId}:`, error);
      }
    }

    this.writeWindowToDoc(spaceId, windowId, updates);
  }

  /**
   * Stream a live drag position to the shared drag-broadcast room so every
   * OTHER collaborator viewing this Space sees the window move in real time.
   * Deliberately does NOT touch the durable `space-windows:` doc or the local
   * zustand store (the dragging client renders from its own local optimistic
   * position) — the durable position is committed once on drag-stop via
   * `updateWindow`. Throttled while dragging.
   *
   * `draggedBy` (optional) rides along in the same broadcast entry so every
   * collaborator can render a "being moved by X" indicator without a second
   * round-trip through the separate presence room — see
   * windowDragIndicatorStore / setupDragReceiver.
   */
  streamWindowDragPosition(spaceId: string, windowId: string, position: { x: number; y: number }, draggedBy?: DraggerIdentity) {
    if (!spaceId) return;
    (window as any).__lastDragWindowId = windowId;
    (window as any).__lastDragPosition = position;

    if (shouldThrottleDragUpdate(windowId)) return;

    const entry: DragBroadcastEntry = draggedBy
      ? { ...position, userId: draggedBy.userId, name: draggedBy.name, color: draggedBy.color }
      : { ...position };

    try {
      this.crdt.update(this.getDragKey(spaceId), { [windowId]: entry }, {
        updateType: 'update',
        preserveOtherConnections: true,
        preserveNoteConnections: true,
      });
    } catch (error) {
      log.error(`[SharedSpaceWindows] Error streaming drag position for window ${windowId}:`, error);
    }
  }

  /**
   * Explicitly signal that a drag has ended, so every OTHER collaborator's
   * "being moved by X" indicator clears immediately instead of waiting on
   * windowDragIndicatorStore's staleness sweep. Removes the window's entry
   * from the drag-broadcast room entirely (the room has no durable
   * persistence, so there's nothing to leave behind).
   *
   * crdtServiceWS.update() flattens nested values onto dot-separated keys
   * (`{x,y,userId,...}` under `windowId` becomes `windowId.x`, `windowId.y`,
   * ...), and its updateType:'remove' path only deletes an EXACT key match —
   * it doesn't know to also sweep `windowId.*` children. Same gotcha
   * presenceService.removePresence already works around: read back the
   * flattened keys that actually exist and remove them explicitly.
   */
  endWindowDrag(spaceId: string, windowId: string) {
    if (!spaceId) return;
    if ((window as any).__lastDragWindowId === windowId) {
      (window as any).__lastDragWindowId = undefined;
      (window as any).__lastDragPosition = undefined;
    }
    const dragKey = this.getDragKey(spaceId);
    try {
      const toRemove: Record<string, null> = { [windowId]: null };
      const doc = this.crdt.getYDoc(dragKey);
      if (doc) {
        const flatData = doc.getMap(dragKey).toJSON();
        for (const flatKey of Object.keys(flatData)) {
          if (flatKey.startsWith(`${windowId}.`)) {
            toRemove[flatKey] = null;
          }
        }
      }
      this.crdt.update(dragKey, toRemove, {
        updateType: 'remove',
        preserveOtherConnections: true,
        preserveNoteConnections: true,
      });
    } catch (error) {
      log.error(`[SharedSpaceWindows] Error ending drag broadcast for window ${windowId}:`, error);
    }
  }

  /**
   * Subscribe to live drag positions broadcast by OTHER collaborators
   * currently dragging a window in this Space. `onDragEnd` fires once a
   * window's entry disappears from the broadcast room (either an explicit
   * `endWindowDrag` removal, or the dragger disconnecting). Returns a
   * cleanup function.
   */
  observeDragPositions(
    spaceId: string,
    onPosition: (windowId: string, entry: DragBroadcastEntry) => void,
    onDragEnd?: (windowId: string) => void,
  ): () => void {
    let previousWindowIds = new Set<string>();
    return this.crdt.connect<Record<string, DragBroadcastEntry>>(this.getDragKey(spaceId), (update) => {
      if (!update.data) return;
      const currentWindowIds = new Set(Object.keys(update.data));
      for (const [windowId, entry] of Object.entries(update.data)) {
        onPosition(windowId, entry);
      }
      if (onDragEnd) {
        for (const windowId of previousWindowIds) {
          if (!currentWindowIds.has(windowId)) onDragEnd(windowId);
        }
      }
      previousWindowIds = currentWindowIds;
    });
  }

  private writeWindowToDoc(spaceId: string, windowId: string, updates: Partial<AnyWindowEntity>) {
    const windowsKey = this.getWindowsKey(spaceId);
    try {
      const doc = this.crdt.getYDoc(windowsKey);
      if (!doc) {
        log.warn('[SharedSpaceWindows] No Y.Doc found for space, cannot update window:', spaceId);
        return;
      }
      doc.transact(() => {
        const windowsMap = doc.getMap('windows');
        const existingWindow = windowsMap.get(windowId);
        if (existingWindow) {
          this.mergeWindowFields(windowsMap, windowId, { ...updates, updatedAt: Date.now() });
        } else {
          const fullWindow = useSpaceStore.getState().windows[windowId];
          if (fullWindow) {
            this.mergeWindowFields(windowsMap, windowId, { ...fullWindow, ...updates, updatedAt: Date.now() });
          } else {
            this.mergeWindowFields(windowsMap, windowId, { ...updates, updatedAt: Date.now() });
            log.warn('[SharedSpaceWindows] Created window in shared CRDT with partial data:', windowId);
          }
        }
      }, this.localOriginId);
    } catch (error) {
      log.error(`[SharedSpaceWindows] Error updating window ${windowId}:`, error);
    }
  }

  /**
   * Wait until `getWindowsKey`'s provider confirms a full server sync round-trip.
   * See spaceCRDTService.waitForSpaceSynced for rationale — callers (e.g.
   * spaceStore's orphaned-child-window cleanup) must not run destructive
   * checks against a snapshot that may still be an incomplete local IDB load.
   */
  async waitForSpaceSynced(spaceId: string, timeoutMs?: number): Promise<boolean> {
    return this.crdt.waitForSynced(this.getWindowsKey(spaceId), timeoutMs);
  }

  private computeConnectionStatus(provider: WebsocketProvider | undefined): SharedWindowsConnectionStatus {
    if (!provider) return 'disconnected';
    if (provider.wsconnected && provider.synced) return 'connected';
    if (provider.wsconnected || (provider as unknown as { wsconnecting?: boolean }).wsconnecting) return 'connecting';
    return 'disconnected';
  }

  /**
   * Current live/sync state of this Space's shared window room — 'connected'
   * once the WebSocket is up AND a full server sync round-trip has
   * completed, 'connecting' while either is pending, 'disconnected' if the
   * socket is down (offline, server unreachable) or no provider exists yet.
   * Powers the always-visible sync indicator in SystemState.
   */
  getConnectionStatus(spaceId: string): SharedWindowsConnectionStatus {
    return this.computeConnectionStatus(this.crdt.getProvider(this.getWindowsKey(spaceId)));
  }

  /**
   * Subscribe to live changes in getConnectionStatus's result. Fires
   * immediately with the current status, then again on every WS
   * connect/disconnect/sync transition. Returns a cleanup function.
   */
  subscribeToConnectionStatus(spaceId: string, onStatus: (status: SharedWindowsConnectionStatus) => void): () => void {
    const provider = this.crdt.getProvider(this.getWindowsKey(spaceId));
    if (!provider) {
      onStatus('disconnected');
      return () => {};
    }

    const emit = () => onStatus(this.computeConnectionStatus(provider));
    emit();

    provider.on('status', emit);
    provider.on('sync', emit);
    provider.on('connection-close', emit);
    provider.on('connection-error', emit);

    return () => {
      provider.off('status', emit);
      provider.off('sync', emit);
      provider.off('connection-close', emit);
      provider.off('connection-error', emit);
    };
  }

  /**
   * Initialize (or warm-reactivate) the shared window room for a space.
   *
   * `getSeedWindows` is called ONLY if the shared room turns out to be
   * confirmed empty after a full server sync — i.e. genuinely the first time
   * ANY collaborator has ever opened this Space's shared canvas. It's a
   * callback (not a plain value) so the caller can defer resolving the
   * migration seed (reading the legacy per-user room) until we actually know
   * it's needed. Returns the merged windows now live in the store, primarily
   * for tests/logging.
   */
  async initializeSpace(
    spaceId: string,
    getSeedWindows: () => Promise<Record<string, AnyWindowEntity>> | Record<string, AnyWindowEntity>,
  ): Promise<Record<string, any>> {
    const existing = this.initializingSpaces.get(spaceId);
    if (existing) {
      log.info('[SharedSpaceWindows] initializeSpace already in flight for %s — awaiting it instead of racing', spaceId);
      await existing;
      return useSpaceStore.getState().windows;
    }

    const promise = this._initializeSpaceInternal(spaceId, getSeedWindows).finally(() => {
      this.initializingSpaces.delete(spaceId);
    });
    this.initializingSpaces.set(spaceId, promise);
    await promise;
    return useSpaceStore.getState().windows;
  }

  private async _initializeSpaceInternal(
    spaceId: string,
    getSeedWindows: () => Promise<Record<string, AnyWindowEntity>> | Record<string, AnyWindowEntity>,
  ): Promise<void> {
    const windowsKey = this.getWindowsKey(spaceId);
    const dragKey = this.getDragKey(spaceId);

    // Warm reactivation: this space's shared Y.Doc has been merging remote
    // updates in the background the whole time it was backgrounded (kept
    // alive by desktopWarmCache) — just re-project its current state.
    if (this.initializedSpaces.has(spaceId)) {
      const doc = this.crdt.getYDoc(windowsKey);
      if (doc) {
        log.log('[SharedSpaceWindows] ♻️ Reactivating warm space (no network wait):', spaceId);
        this._projectDocToStore(doc);
        return;
      }
      log.warn('[SharedSpaceWindows] initializedSpaces had stale entry with no doc, re-initializing:', spaceId);
      this.initializedSpaces.delete(spaceId);
    }

    const existingObserverCleanup = this.spaceObservers.get(spaceId);
    if (existingObserverCleanup) {
      console.error('[SharedSpaceWindows] ⚠️ Cleaning up existing observer - this indicates duplicate initialization!');
      existingObserverCleanup();
      this.spaceObservers.delete(spaceId);
    }
    const existingDragCleanup = this.dragReceivers.get(spaceId);
    if (existingDragCleanup) {
      existingDragCleanup();
      this.dragReceivers.delete(spaceId);
    }

    this.initializedSpaceKeys.set(spaceId, { windowsKey, dragKey });
    this.initializedSpaces.add(spaceId);

    this.dragReceivers.set(spaceId, this.setupDragReceiver(spaceId));

    const doc = this.crdt.ensureInitialized(windowsKey);
    const provider = this.crdt.getProvider(windowsKey);
    await this.crdt.waitForIdbSynced(windowsKey);

    const windowsMap = doc.getMap('windows');
    const crdtHasData = windowsMap.size > 0;

    log.info('[SharedSpaceWindows] 🔍 Shared room state check:', {
      spaceId,
      crdtHasData,
      crdtWindowCount: windowsMap.size,
      providerSynced: provider?.synced,
    });

    if (crdtHasData || !provider?.synced) {
      // Either IDB already has collaborators' data, or we don't yet know the
      // server's state — never seed over it. setupSpaceObserver (below) will
      // fire once WS sync completes and any remote state arrives.
      log.info('[SharedSpaceWindows] 🤝 Using existing shared room state (crdtHasData=%s, providerSynced=%s)', crdtHasData, provider?.synced);
      this._projectDocToStore(doc);
    } else {
      // WS confirmed the shared room is empty — this is genuinely the first
      // time any collaborator has opened this Space's shared canvas. Seed
      // it from the migration source (typically this user's own pre-existing
      // per-user window layout).
      const seedWindows = await getSeedWindows();
      log.info('[SharedSpaceWindows] 🎬 Shared room empty after WS sync — seeding from migration source:', {
        spaceId,
        seedWindowCount: Object.keys(seedWindows || {}).length,
      });

      // Single transaction (see mergeWindowFields docstring): if a fast
      // duplicate initializeSpace call raced this one (the reentrancy guard
      // above should prevent that, but defense in depth matters here — see
      // spaceCRDTService.manyWindowsSeed.test.ts for the exact bug this
      // atomicity fixes), a concurrent observer must never see a partially
      // seeded windows map.
      doc.transact(() => {
        for (const [windowId, win] of Object.entries(seedWindows || {})) {
          this.mergeWindowFields(windowsMap, windowId, win as Record<string, any>);
        }
      }, this.localOriginId);

      useSpaceStore.setState((state) => ({
        windows: seedWindows || {},
        activeSpace: state.activeSpace ? { ...state.activeSpace, windows: seedWindows || {} } : state.activeSpace,
      }));

      this._hydrateZIndexes(seedWindows || {});
    }

    this.setupSpaceObserver(spaceId, doc);
    log.info('[SharedSpaceWindows] ✅ Shared window room initialization complete (room=%s)', windowsKey);
  }

  private setupDragReceiver(spaceId: string): () => void {
    const staleSweep = setInterval(() => {
      useWindowDragIndicatorStore.getState().clearStale(DRAG_INDICATOR_STALE_MS);
    }, DRAG_INDICATOR_SWEEP_MS);

    // Mirrors exactly the windowIds this receiver currently believes are
    // being dragged, so the raw-update fallback below can clear precisely
    // this Space's indicators without touching another (warm-cached) Space's
    // drag receiver sharing the same global windowDragIndicatorStore.
    const knownDraggingWindowIds = new Set<string>();

    const unobserve = this.observeDragPositions(
      spaceId,
      (windowId, entry) => {
        if (entry.userId && entry.name) {
          knownDraggingWindowIds.add(windowId);
          useWindowDragIndicatorStore.getState().setDragger(windowId, {
            userId: entry.userId,
            name: entry.name,
            color: entry.color || '#888',
          });
        }

        if ((window as any).__isUpdatingStore) return;
        // Skip our own echoed drag position.
        if ((window as any).__lastDragWindowId === windowId &&
            (window as any).__lastDragPosition &&
            (window as any).__lastDragPosition.x === entry.x &&
            (window as any).__lastDragPosition.y === entry.y) {
          return;
        }
        (window as any).__isUpdatingStore = true;
        try {
          const store = useSpaceStore.getState();
          if (store.windows[windowId]) {
            store._applyLocalWindowUpdate(windowId, { position: { x: entry.x, y: entry.y } }, {
              source: 'crdt',
              syncCRDT: false,
              log: false,
            });
          }
        } finally {
          (window as any).__isUpdatingStore = false;
        }
      },
      (windowId) => {
        knownDraggingWindowIds.delete(windowId);
        useWindowDragIndicatorStore.getState().clearDragger(windowId);
      },
    );

    // Fallback for the one case observeDragPositions' snapshot diffing can't
    // see: crdtServiceWS's debounced Y.Map observer treats an empty map as
    // "no update" (ymapToObject returns null — see presenceService's own
    // workaround for the same quirk), so when the LAST dragger in this Space
    // removes their entry, the debounced observer never fires at all and
    // remote viewers would otherwise wait out the staleness sweep instead of
    // clearing immediately. A raw Y.Doc update always fires regardless.
    const dragKey = this.getDragKey(spaceId);
    const doc = this.crdt.getYDoc(dragKey);
    let rawUpdateHandler: (() => void) | null = null;
    if (doc) {
      rawUpdateHandler = () => {
        if (knownDraggingWindowIds.size > 0 && doc.getMap(dragKey).size === 0) {
          for (const windowId of knownDraggingWindowIds) {
            useWindowDragIndicatorStore.getState().clearDragger(windowId);
          }
          knownDraggingWindowIds.clear();
        }
      };
      doc.on('update', rawUpdateHandler);
    }

    return () => {
      clearInterval(staleSweep);
      unobserve();
      if (doc && rawUpdateHandler) doc.off('update', rawUpdateHandler);
    };
  }

  private setupSpaceObserver(spaceId: string, doc: Y.Doc) {
    const windowsKey = this.getWindowsKey(spaceId);
    const windowsMap = doc.getMap('windows');
    const provider = this.crdt.getProvider(windowsKey);

    let isUpdatingFromCRDT = false;

    const observer = (_update: Uint8Array, origin: any) => {
      if (isUpdatingFromCRDT) return;
      if (origin === this.localOriginId) return;

      const activeSpaceId = useSpaceStore.getState().activeSpace?.id;
      if (activeSpaceId && activeSpaceId !== spaceId) {
        console.warn('[SharedSpaceWindows][SpaceMismatch] observer fire for stale space — ignoring', {
          observerSpaceId: spaceId,
          activeSpaceId,
        });
        return;
      }

      try {
        isUpdatingFromCRDT = true;

        // Live viewMode sync — see broadcastViewMode. `applyRemoteViewMode`
        // deliberately does NOT reproject window geometry (that would race
        // the sender's own reprojection, which already wrote the
        // authoritative new-family positions into `windows` below via the
        // normal diff-merge) — it only updates local UI bookkeeping
        // (viewMode, sidebar state). See applyLayoutForViewMode's `isRemote`
        // handling in spaceStore.
        const remoteViewMode = doc.getMap('meta').get('viewMode');
        if (typeof remoteViewMode === 'string') {
          useSpaceStore.getState().applyRemoteViewMode(remoteViewMode);
        }

        const windowsFromCRDT: Record<string, any> = {};
        windowsMap.forEach((win: any, windowId: string) => {
          try {
            windowsFromCRDT[windowId] = win?.toJSON ? win.toJSON() : JSON.parse(JSON.stringify(win));
          } catch (e) {
            log.error('[SharedSpaceWindows] Failed to deserialize window from CRDT:', windowId, e);
            windowsFromCRDT[windowId] = win;
          }
        });

        const currentSpaceStore = useSpaceStore.getState();

        // Same delete-diffing rule as spaceCRDTService: only trust "not
        // present" as "was deleted" once the provider has confirmed a full
        // server sync, otherwise a mid-seed snapshot could wipe windows that
        // simply haven't arrived yet.
        if (provider?.synced) {
          const crdtWindowIds = new Set(Object.keys(windowsFromCRDT));
          for (const windowId of Object.keys(currentSpaceStore.windows)) {
            if (!crdtWindowIds.has(windowId)) {
              currentSpaceStore._applyLocalWindowDelete(windowId, {
                source: 'crdt',
                syncCRDT: false,
                log: false,
              });
            }
          }
        } else {
          log.info('[SharedSpaceWindows] Skipping delete-diff — provider not yet synced, snapshot may be incomplete');
        }

        const currentlyDraggedWindowId = (window as any).__lastDragWindowId as string | undefined;
        const filteredCRDTWindows: Record<string, any> = {};
        for (const [windowId, crdtWindow] of Object.entries(windowsFromCRDT)) {
          if (windowId === currentlyDraggedWindowId) continue;

          const localWindow = currentSpaceStore.windows[windowId];
          const localUpdatedAt = normalizeUpdatedAt((localWindow as any)?.updatedAt);
          const crdtUpdatedAt = normalizeUpdatedAt((crdtWindow as any)?.updatedAt);
          if (localUpdatedAt > crdtUpdatedAt) {
            log.info('[SharedSpaceWindows] skip-stale-window (local newer):', windowId, { localUpdatedAt, crdtUpdatedAt });
            continue;
          }
          filteredCRDTWindows[windowId] = crdtWindow;
        }

        useSpaceStore.setState((state) => ({
          windows: { ...state.windows, ...filteredCRDTWindows },
          activeSpace: state.activeSpace ? {
            ...state.activeSpace,
            windows: { ...state.windows, ...filteredCRDTWindows },
          } : state.activeSpace,
        }));
      } finally {
        isUpdatingFromCRDT = false;
      }
    };

    doc.on('update', observer);
    this.spaceObservers.set(spaceId, () => {
      doc.off('update', observer);
      log.info('[SharedSpaceWindows] 🧹 Removed observer for space:', spaceId);
    });

    log.info(`[SharedSpaceWindows] ✅ Observer set up for bidirectional sync (room=${windowsKey})`);
  }

  private _projectDocToStore(doc: Y.Doc): Record<string, any> {
    const windowsMap = doc.getMap('windows');
    const windowsFromCRDT: Record<string, any> = {};
    windowsMap.forEach((win: any, windowId: string) => {
      try {
        windowsFromCRDT[windowId] = win?.toJSON ? win.toJSON() : JSON.parse(JSON.stringify(win));
      } catch (e) {
        log.error('[SharedSpaceWindows] Failed to deserialize window from CRDT:', windowId, e);
        windowsFromCRDT[windowId] = win;
      }
    });

    useSpaceStore.setState((state) => ({
      windows: windowsFromCRDT,
      activeSpace: state.activeSpace ? { ...state.activeSpace, windows: windowsFromCRDT } : state.activeSpace,
    }));

    this._hydrateZIndexes(windowsFromCRDT);
    return windowsFromCRDT;
  }

  private _hydrateZIndexes(windows: Record<string, any>) {
    const family = layoutFamilyOf(useSpaceStore.getState().activeSpace?.settings?.viewMode) ?? undefined;
    useSpatialUIStore.getState().hydrateZIndexesFromWindows(windows, family);
  }

  /**
   * Disconnect this tab's connection to a space's shared window room. Safer
   * for multi-tab scenarios than disconnectAll — mirrors
   * spaceCRDTService.disconnectSpace.
   */
  disconnectSpace(spaceId: string, tabId: string) {
    log.info(`[SharedSpaceWindows] 🧹 Disconnecting space ${spaceId} for tab ${tabId}`);
    try {
      const observerCleanup = this.spaceObservers.get(spaceId);
      if (observerCleanup) {
        observerCleanup();
        this.spaceObservers.delete(spaceId);
      }
      const dragCleanup = this.dragReceivers.get(spaceId);
      if (dragCleanup) {
        dragCleanup();
        this.dragReceivers.delete(spaceId);
      }
      this.initializedSpaces.delete(spaceId);
      this.connectionListeners.delete(spaceId);
      useWindowDragIndicatorStore.getState().reset();

      const stored = this.initializedSpaceKeys.get(spaceId);
      this.initializedSpaceKeys.delete(spaceId);
      if (stored) {
        try { this.crdt.disconnect(stored.windowsKey); } catch (error) {
          log.warn(`[SharedSpaceWindows] Error disconnecting ${stored.windowsKey}:`, error);
        }
        try { this.crdt.disconnect(stored.dragKey); } catch (error) {
          log.warn(`[SharedSpaceWindows] Error disconnecting ${stored.dragKey}:`, error);
        }
      }
      log.info(`[SharedSpaceWindows] ✅ Successfully disconnected space ${spaceId}`);
    } catch (err) {
      log.warn(`[SharedSpaceWindows] Error disconnecting space ${spaceId} for tab ${tabId}:`, err);
    }
  }

  disconnectAll() {
    log.info('[SharedSpaceWindows] Disconnecting all');
    this.spaceObservers.forEach((cleanup) => { try { cleanup(); } catch {} });
    this.spaceObservers.clear();
    this.dragReceivers.forEach((cleanup) => { try { cleanup(); } catch {} });
    this.dragReceivers.clear();
    this.connectionListeners.clear();
    useWindowDragIndicatorStore.getState().reset();

    const keysSnapshot = new Map(this.initializedSpaceKeys);
    this.initializedSpaces.clear();
    this.initializedSpaceKeys.clear();
    for (const [, { windowsKey, dragKey }] of keysSnapshot) {
      try { this.crdt.disconnect(windowsKey); } catch (err) { log.warn(`[SharedSpaceWindows] Error disconnecting ${windowsKey}:`, err); }
      try { this.crdt.disconnect(dragKey); } catch (err) { log.warn(`[SharedSpaceWindows] Error disconnecting ${dragKey}:`, err); }
    }
  }

  getSpaceConnectionInfo(spaceId: string): SharedSpaceConnectionInfo {
    const stored = this.initializedSpaceKeys.get(spaceId);
    if (!stored) {
      return { spaceId, connectionCount: 0, providerStatus: 'disconnected', lastUpdate: Date.now() };
    }
    const provider = this.crdt.getProvider(stored.windowsKey);
    return {
      spaceId,
      connectionCount: provider ? 1 : 0,
      providerStatus: provider ? (provider.wsconnected ? 'connected' : 'connecting') : 'disconnected',
      lastUpdate: Date.now(),
    };
  }

  onConnectionChange(spaceId: string, callback: (info: SharedSpaceConnectionInfo) => void): () => void {
    if (!this.connectionListeners.has(spaceId)) this.connectionListeners.set(spaceId, []);
    const listeners = this.connectionListeners.get(spaceId)!;
    listeners.push(callback);
    callback(this.getSpaceConnectionInfo(spaceId));

    if (!this.connectionPollIntervals.has(spaceId)) {
      const interval = setInterval(() => {
        const current = this.connectionListeners.get(spaceId);
        if (!current || current.length === 0) return;
        const info = this.getSpaceConnectionInfo(spaceId);
        for (const listener of current) {
          try { listener(info); } catch (error) { log.error('[SharedSpaceWindows] Error in connection listener:', error); }
        }
      }, 2000);
      this.connectionPollIntervals.set(spaceId, interval);
    }

    return () => {
      const current = this.connectionListeners.get(spaceId);
      if (!current) return;
      const idx = current.indexOf(callback);
      if (idx > -1) current.splice(idx, 1);
      if (current.length === 0) {
        this.connectionListeners.delete(spaceId);
        const interval = this.connectionPollIntervals.get(spaceId);
        if (interval) {
          clearInterval(interval);
          this.connectionPollIntervals.delete(spaceId);
        }
      }
    };
  }
}

export const sharedSpaceWindowsCRDTService = new SharedSpaceWindowsCRDTService(crdtServiceWS);
